import React, { useState } from "react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";

export const UserForm = ({ user, sites, studies = [], onSave, onCancel, isEdit = false }) => {
  const [formData, setFormData] = useState({
    name: user?.name || '',
    email: user?.email || '',
    role: user?.role || 'site_coordinator',
    sites: user?.sites || [],
    studies: user?.studies || []
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(user?.id, formData);
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSiteSelection = (siteId, checked) => {
    if (checked) {
      setFormData(prev => ({ ...prev, sites: [...prev.sites, siteId] }));
    } else {
      setFormData(prev => ({ ...prev, sites: prev.sites.filter(id => id !== siteId) }));
    }
  };

  const handleStudySelection = (studyId, checked) => {
    if (checked) {
      setFormData(prev => ({ ...prev, studies: [...prev.studies, studyId] }));
    } else {
      setFormData(prev => ({ ...prev, studies: prev.studies.filter(id => id !== studyId) }));
    }
  };

  const selectAllStudies = () => {
    setFormData(prev => ({ ...prev, studies: studies.map(s => s.id) }));
  };

  const clearAllStudies = () => {
    setFormData(prev => ({ ...prev, studies: [] }));
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4" data-testid="user-form">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label htmlFor="name">Full Name</Label>
          <Input
            id="name"
            data-testid="user-form-name"
            value={formData.name}
            onChange={(e) => handleChange('name', e.target.value)}
            placeholder="Enter full name"
            required
          />
        </div>
        <div>
          <Label htmlFor="email">Email Address</Label>
          <Input
            id="email"
            type="email"
            data-testid="user-form-email"
            value={formData.email}
            onChange={(e) => handleChange('email', e.target.value)}
            placeholder="Enter email address"
            required
          />
        </div>
      </div>

      <div>
        <Label htmlFor="role">Role</Label>
        <Select value={formData.role} onValueChange={(value) => handleChange('role', value)}>
          <SelectTrigger data-testid="user-form-role">
            <SelectValue placeholder="Select role" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="site_coordinator">Site Coordinator</SelectItem>
            <SelectItem value="study_lead">Study Lead</SelectItem>
            <SelectItem value="qc">QC</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {formData.role === 'site_coordinator' && (
        <div>
          <Label>Assigned Sites</Label>
          <div className="grid grid-cols-2 gap-2 mt-2 max-h-48 overflow-y-auto border rounded-md p-3">
            {sites.map(site => (
              <div key={site.id} className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id={`site-${site.id}`}
                  checked={formData.sites.includes(site.id)}
                  onChange={(e) => handleSiteSelection(site.id, e.target.checked)}
                  className="rounded border-gray-300"
                />
                <Label htmlFor={`site-${site.id}`} className="text-sm cursor-pointer">
                  {site.name}
                </Label>
              </div>
            ))}
          </div>
        </div>
      )}

      {formData.role === 'qc' && (
        <div>
          <div className="flex items-center justify-between mb-1">
            <Label>Assigned Studies</Label>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={selectAllStudies}
                className="text-xs text-teal-600 hover:text-teal-800 font-medium"
                data-testid="select-all-studies"
              >
                Select All
              </button>
              <span className="text-gray-300">|</span>
              <button
                type="button"
                onClick={clearAllStudies}
                className="text-xs text-gray-500 hover:text-gray-700 font-medium"
                data-testid="clear-all-studies"
              >
                Clear
              </button>
            </div>
          </div>
          <p className="text-xs text-gray-500 mb-2">
            QC members are assigned to studies. They will handle QC tasks for all visits under selected studies.
          </p>
          <div className="grid grid-cols-1 gap-2 max-h-56 overflow-y-auto border rounded-md p-3" data-testid="study-assignment-list">
            {studies.map(study => (
              <div key={study.id} className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id={`study-${study.id}`}
                  checked={formData.studies.includes(study.id)}
                  onChange={(e) => handleStudySelection(study.id, e.target.checked)}
                  className="rounded border-gray-300"
                />
                <Label htmlFor={`study-${study.id}`} className="text-sm cursor-pointer">
                  {study.name}
                  {study.protocol_number && (
                    <span className="text-xs text-gray-400 ml-1">({study.protocol_number})</span>
                  )}
                </Label>
              </div>
            ))}
            {studies.length === 0 && (
              <p className="text-gray-400 text-sm text-center py-2">No studies available</p>
            )}
          </div>
          <p className="text-xs text-gray-400 mt-1">{formData.studies.length} of {studies.length} studies selected</p>
        </div>
      )}

      <div className="flex justify-end space-x-2 pt-4">
        <Button type="button" variant="outline" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit" data-testid="user-form-submit">
          {isEdit ? 'Update User' : 'Create User'}
        </Button>
      </div>
    </form>
  );
};
